import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import ProductAllVariantsModal from './ProductAllVariantModal'
import { ProductTableLite } from '../ProductTableLite'
import { useTablePersistence } from '../useTablePersistence'
import { exportToCSV } from '../exportCSV'
import {
  CatalogColumn,
  SortState,
  FilterState,
  PaginationState,
  SelectedColumn,
  ProductVariant,
} from '../types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_STATE } from '../defaultsFromLegacy'
import { normalizeFilterInputType, augmentSyncStatusFilterOptions } from '../../listing-product-table-lite/filterUtils'
import {
  getAgProductColumnsAsync,
  getListingAgProductsAsync,
  deleteVariantAsync
} from 'src/redux/actions/omsProductActions'
import { getSortOrder } from 'src/marketConnect/helpers'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { toast } from 'react-toastify'
import {
  getListingProductBulkActionOptions,
  isListingProductBulkActionPlatform,
} from 'src/utils/listingProductBulkActions'

// Grouped-lite tab: data from groupedViewLiteSlice + getListingAgProductsAsync / getAgProductColumnsAsync; UI uses ProductTableLite (no collapse grouping).

interface GroupedViewLiteContainerProps {
  id?: string
  platformId?: string
  currentChannelData?: any
  mainStore?: boolean
  accountingStore?: boolean
  shippingStore?: boolean
  erpStore?: boolean
  tabsData?: any[]
  onLinkClick?: (rowData: any, rowIndex: number) => void
  /** When true, show synthetic Actions column (Grouped View Lite + OMS). */
  isOmsStore?: boolean
  onGroupedLiteEditProduct?: (row: any) => void
  /** When false, Edit is disabled (e.g. !isWrite). */
  groupedLiteEditEnabled?: boolean
  linkingCompleted?: boolean
}

const DEFAULT_VISIBLE_FIELDS = [
  'name',
  'sku',
  'isCreatedByCommercium',
  'total_variants',
  'api_status',
  'total_available_quantity',
  'total_reserve_quantity',
  'created_at',
  'updated_at',
]

/** Synthetic column for Grouped View Lite + OMS only; not from variant API catalog. */
const GROUPED_LITE_ACTIONS_FIELD = 'grouped_lite_actions'
const GROUPED_LITE_SUPPORT_FIELDS = ['id', 'is_editable']

export const GroupedViewLiteContainer: FC<GroupedViewLiteContainerProps> = ({
  id,
  platformId,
  currentChannelData,
  mainStore = false,
  accountingStore = false,
  shippingStore = false,
  erpStore = false,
  tabsData = [],
  onLinkClick,
  isOmsStore = false,
  onGroupedLiteEditProduct,
  groupedLiteEditEnabled = true,
  linkingCompleted = false,
}) => {
  const navigate = useNavigate()
  const [showVariantsModal, setShowVariantsModal] = useState(false)
  const [selectedVariantProduct, setSelectedVariantProduct] = useState<any | null>(null)

  const [showDeleteVariantModal, setShowDeleteVariantModal] = useState(false)
  const [selectedVariantToDelete, setSelectedVariantToDelete] = useState<any>(null)
  const [isDeletingVariant, setIsDeletingVariant] = useState(false)
  const [variantRefreshToggle, setVariantRefreshToggle] = useState(false)

  const handleOpenGroupedLiteVariantsModal = useCallback((row: any) => {
    setSelectedVariantProduct(row)
    setShowVariantsModal(true)
  }, [])

  const handleCloseGroupedLiteVariantsModal = useCallback(() => {
    setShowVariantsModal(false)
    setSelectedVariantProduct(null)
  }, [])

  const effectiveIsOmsStore = Boolean(isOmsStore)

  const groupedLiteEditHandler = useMemo(() => {
    if (!effectiveIsOmsStore) return undefined
    if (onGroupedLiteEditProduct) return onGroupedLiteEditProduct
    return undefined
  }, [effectiveIsOmsStore, onGroupedLiteEditProduct])

  const dispatch = useAppDispatch()
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)
  const groupedViewLiteSlice = useAppSelector((state: any) => state.groupedViewLiteSlice)

  const userId = fetchedUserAccountData?.id || 'guest'

  const persistence = useTablePersistence({
    userId,
    platformId: platformId || '0',
    channelId: `${id || '0'}:grouped-lite`,
  })

  const initialState = persistence.getInitialState()

  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>(
    initialState.selectedColumns || []
  )
  const [sortState, setSortState] = useState<SortState | null>(
    initialState.sortState || null
  )
  const [filterState, setFilterState] = useState<FilterState>(
    initialState.filterState || { globalSearch: '', columnFilters: [] }
  )
  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 1,
    pageSize: initialState.pageSize || DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })
  const [selectedRowIds, setSelectedRowIds] = useState<Array<number | string>>([])
  const [selectedAction, setSelectedAction] = useState('')

  const mandatoryColumns = useMemo(
    () => (effectiveIsOmsStore ? ['name', GROUPED_LITE_ACTIONS_FIELD] : ['name']),
    [effectiveIsOmsStore]
  )

  const groupedLiteDefaultVisibleFields = useMemo(
    () =>
      effectiveIsOmsStore
        ? [...DEFAULT_VISIBLE_FIELDS, GROUPED_LITE_ACTIONS_FIELD]
        : DEFAULT_VISIBLE_FIELDS,
    [effectiveIsOmsStore]
  )

  const productsRows = groupedViewLiteSlice?.products?.rows || []
  const productsColumns = groupedViewLiteSlice?.products?.columns || []
  const productColumns = groupedViewLiteSlice?.productColumns?.columns || []
  const total = groupedViewLiteSlice?.products?.total || 0

  const loading =
    Boolean(groupedViewLiteSlice?.products?.loading) ||
    Boolean(groupedViewLiteSlice?.productColumns?.loading)

  const error =
    groupedViewLiteSlice?.products?.error ||
    groupedViewLiteSlice?.productColumns?.error ||
    null

  const displayRows = useMemo(
    () => (Array.isArray(productsRows) ? productsRows : []),
    [productsRows]
  )

  const selectedApiColumns = useMemo(() => {
    const visibleFields = [...selectedColumns]
      .sort((a, b) => a.order - b.order)
      .map((column) => column.field)
      .filter((field) => field && field !== GROUPED_LITE_ACTIONS_FIELD)

    if (visibleFields.length === 0) return undefined

    return Array.from(new Set([...visibleFields, ...GROUPED_LITE_SUPPORT_FIELDS]))
  }, [selectedColumns])

  const apiColumnFilters = useMemo(
    () =>
      (filterState.columnFilters || [])
        .map((filter) => {
          let field = filter.field || ''
          if (field === 'product_name') field = 'name'
          
          if (field === GROUPED_LITE_ACTIONS_FIELD || field === 'link_actions') {
            return null
          }

          if (field === 'linking_status') {
            const valStr = String(filter.value).toLowerCase().trim()
            let val: any = filter.value
            if (filter.value === true || valStr === 'true' || valStr === 'linked') {
              val = 'True'
            } else if (filter.value === false || valStr === 'false' || valStr === 'unlinked') {
              val = 'False'
            } else if (typeof filter.value === 'string' && filter.value.trim() !== '') {
              val = filter.value.toUpperCase()
            }
            return { field: 'linking_status', value: val, ...(filter.operator ? { operator: filter.operator } : {}) }
          }

          if (!field || filter.value === '' || filter.value === null || filter.value === undefined) {
            return null
          }
          return { field, value: filter.value, ...(filter.operator ? { operator: filter.operator } : {}) }
        })
        .filter((filter): filter is { field: string; value: any; operator?: string } => Boolean(filter)),
    [filterState.columnFilters]
  )

  const areSelectedColumnsEqual = useCallback((a: SelectedColumn[], b: SelectedColumn[]) => {
    if (a === b) return true
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i += 1) {
      const left = a[i]
      const right = b[i]
      if (
        left.field !== right.field ||
        left.order !== right.order ||
        left.width !== right.width ||
        (left.pinned || null) !== (right.pinned || null)
      ) {
        return false
      }
    }

    return true
  }, [])

  const normalizeSelectedColumns = useCallback(
    (columns: SelectedColumn[]) => {
      const sorted = [...(columns || [])].sort((a, b) => a.order - b.order)
      const dedupedFields = new Set<string>()
      const next: SelectedColumn[] = []
      let actionsColumn: SelectedColumn | null = null

      sorted.forEach((column) => {
        if (!column?.field || dedupedFields.has(column.field)) return
        dedupedFields.add(column.field)

        if (column.field === GROUPED_LITE_ACTIONS_FIELD) {
          actionsColumn = column
          return
        }

        next.push(column)
      })

      if (effectiveIsOmsStore) {
        next.push({
          ...(actionsColumn || { field: GROUPED_LITE_ACTIONS_FIELD, order: next.length }),
          field: GROUPED_LITE_ACTIONS_FIELD,
          pinned: null,
        })
      }

      return next.map((column, index) => ({ ...column, order: index }))
    },
    [effectiveIsOmsStore]
  )

  useEffect(() => {
    persistence.markInitialized()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      total,
      totalPages: prev.pageSize > 0 ? Math.ceil(total / prev.pageSize) : 0,
    }))
  }, [total, paginationState.pageSize])

  useEffect(() => {
    setPaginationState((prev) => {
      if (prev.totalPages <= 0) {
        return prev.page === 1 ? prev : { ...prev, page: 1 }
      }
      if (prev.page <= prev.totalPages) return prev
      return { ...prev, page: prev.totalPages }
    })
  }, [paginationState.totalPages])

  useEffect(() => {
    if (persistence.isInitialized()) {
      persistence.savePersistedState({
        selectedColumns,
        sortState,
        filterState,
        pageSize: paginationState.pageSize,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, sortState, filterState, paginationState.pageSize])

  useEffect(() => {
    if (!id) return

    dispatch(
      getAgProductColumnsAsync({
        platformId: Number(id),
        page: 1,
        limit: 10,
      }) as any
    )
  }, [dispatch, id])

  useEffect(() => {
    if (!id || linkingCompleted === true) return

    dispatch(
      getListingAgProductsAsync({
        platformId: Number(id),
        page: paginationState.page,
        limit: paginationState.pageSize,
        search_string: filterState.globalSearch || '',
        sort_by: sortState?.field === 'product_name' ? 'name' : (sortState?.field || ''),
        sort_order: getSortOrder(sortState?.direction),
        columns: selectedApiColumns,
        columnFilters: apiColumnFilters,
      }) as any
    )
  }, [
    dispatch,
    id,
    paginationState.page,
    paginationState.pageSize,
    filterState.globalSearch,
    apiColumnFilters,
    sortState,
    selectedApiColumns,
    linkingCompleted,
  ])

  const rawColumns = productColumns.length > 0 ? productColumns : productsColumns

  const normalizedCatalogFromApi: CatalogColumn[] = useMemo(() => {
    return (rawColumns || [])
      .filter((() => {
        const seenFields = new Set<string>()
        return (col: any) => {
          if (!Boolean(col?.field)) return false
          const f = String(col.field)
          if (f === GROUPED_LITE_ACTIONS_FIELD) return false
          if (seenFields.has(f)) return false
          seenFields.add(f)
          return true
        }
      })())
      .map((col: any) => {
        const field = String(col.field)
        const rawVisibility =
          col?.allow_visiblity || col?.allow_visibility || col?.allowVisibility || 'YES'
        const allowVisibility: 'YES' | 'NO' | 'ALWAYS_VISIBLE' =
          rawVisibility === 'ALWAYS_VISIBLE'
            ? 'ALWAYS_VISIBLE'
            : rawVisibility === 'NO'
              ? 'NO'
              : 'YES'
        const advance = col?.advance_option || {}
        const filterInputType = normalizeFilterInputType(advance, field)
        const headerName = String(
          col?.headerName || col?.label || col?.name || field
        ).trim()
        const title = headerName || field
        const columnType: 'date' | 'text' =
          filterInputType === 'DATE_RANGE' || field === 'created_at' || field === 'updated_at'
            ? 'date'
            : 'text'

        const normalized: CatalogColumn = {
          ...col,
          field,
          id: col?.id || `api-${field}`,
          name: title,
          label: title,
          headerName: title,
          sortable:
            typeof advance?.allow_sorting === 'boolean'
              ? advance.allow_sorting
              : col?.sortable ?? true,
          filterable:
            filterInputType === 'DATE_RANGE' || field === 'created_at' || field === 'updated_at'
              ? true
              : typeof advance?.allow_filter === 'boolean'
              ? advance.allow_filter
              : col?.filterable ?? true,
          resizable:
            typeof advance?.allow_resize === 'boolean'
              ? advance.allow_resize
              : col?.resizable ?? true,
          filterInputType,
          filterOptions: augmentSyncStatusFilterOptions(
            field,
            Array.isArray(advance?.filter_options) ? advance.filter_options : []
          ),
          filterOperators: Array.isArray(advance?.filter_operators) ? advance.filter_operators : [],
          allowVisibility,
          allow_visiblity: allowVisibility,
          fixed: allowVisibility === 'ALWAYS_VISIBLE' || Boolean(col?.fixed),
          type: columnType,
          width: field === 'name' ? 260 : 170,
          advance_option: advance,
        }
        return normalized
      })
  }, [rawColumns])

  const catalogColumns: CatalogColumn[] = useMemo(() => {
    if (!effectiveIsOmsStore) return normalizedCatalogFromApi
    const synthetic: CatalogColumn = {
      id: 'grouped-lite-actions',
      name: 'Actions',
      field: GROUPED_LITE_ACTIONS_FIELD,
      label: 'Actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      resizable: false,
      filterInputType: 'TEXT',
      filterOptions: [],
      filterOperators: [],
      allowVisibility: 'ALWAYS_VISIBLE',
      allow_visiblity: 'ALWAYS_VISIBLE',
      fixed: false,
      type: 'custom',
      width: 130,
    }
    return [...normalizedCatalogFromApi, synthetic]
  }, [normalizedCatalogFromApi, effectiveIsOmsStore])

  useEffect(() => {
    if (!effectiveIsOmsStore) {
      setSelectedColumns((prev) => {
        const filtered = prev.filter((c) => c.field !== GROUPED_LITE_ACTIONS_FIELD)
        if (filtered.length === prev.length) return prev
        return filtered.map((c, idx) => ({ ...c, order: idx }))
      })
      setFilterState((prev) => ({
        ...prev,
        columnFilters: prev.columnFilters.filter((f) => f.field !== GROUPED_LITE_ACTIONS_FIELD),
      }))
      return
    }

    if (catalogColumns.length === 0) return

    setSelectedColumns((prev) => {
      const next = normalizeSelectedColumns(prev)
      return areSelectedColumnsEqual(prev, next) ? prev : next
    })
  }, [effectiveIsOmsStore, catalogColumns, normalizeSelectedColumns, areSelectedColumnsEqual])

  useEffect(() => {
    const realColumns = selectedColumns.filter((c) => c.field !== GROUPED_LITE_ACTIONS_FIELD)
    if (realColumns.length > 0 || catalogColumns.length === 0) return

    const defaults = catalogColumns
      .filter((col) => {
        if (!col.field) return false
        if (col.allowVisibility === 'NO') return false
        if (col.allowVisibility === 'ALWAYS_VISIBLE') return true
        return groupedLiteDefaultVisibleFields.includes(col.field)
      })
      .map((col, index) => ({
        field: col.field,
        order: index,
      }))

    const next = normalizeSelectedColumns(defaults)
    setSelectedColumns(next)
  }, [catalogColumns, selectedColumns.length, groupedLiteDefaultVisibleFields, normalizeSelectedColumns])

  const getColumnByField = useCallback(
    (field: string) => catalogColumns.find((col) => col.field === field),
    [catalogColumns]
  )

  const handleColumnsChange = useCallback(
    (columns: SelectedColumn[]) => {
      const next = normalizeSelectedColumns(columns)
      setSelectedColumns((prev) =>
        areSelectedColumnsEqual(prev, next) ? prev : next
      )
    },
    [areSelectedColumnsEqual, normalizeSelectedColumns]
  )

  const handleReorderColumns = useCallback((dragIndex: number, hoverIndex: number) => {
    setSelectedColumns((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order)
      const dragged = sorted[dragIndex]
      if (!dragged) return prev

      sorted.splice(dragIndex, 1)
      sorted.splice(hoverIndex, 0, dragged)

      return normalizeSelectedColumns(sorted.map((item, index) => ({
        ...item,
        order: index,
      })))
    })
  }, [normalizeSelectedColumns])

  const handlePinColumn = useCallback(
    (field: string, position: 'left' | 'right' | null) => {
      setSelectedColumns((prev) => {
        const next = prev.map((col) =>
          col.field === field ? { ...col, pinned: position } : col
        )
        return normalizeSelectedColumns(next)
      })
    },
    [normalizeSelectedColumns]
  )

  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortState({ field, direction })
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters)
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setPaginationState((prev) => {
      const minPage = 1
      const maxPage = prev.totalPages > 0 ? prev.totalPages : 1
      const nextPage = Math.min(Math.max(page, minPage), maxPage)
      return nextPage === prev.page ? prev : { ...prev, page: nextPage }
    })
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPaginationState((prev) => {
      const nextPageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : prev.pageSize
      return { ...prev, pageSize: nextPageSize, page: 1 }
    })
  }, [])

  const handleExportCSV = useCallback(() => {
    exportToCSV(displayRows, selectedColumns, getColumnByField as any, id || '0', tabsData)
  }, [displayRows, selectedColumns, getColumnByField, id, tabsData])

  const handleResetColumns = useCallback(() => {
    setSortState(DEFAULT_SORT_STATE)
    setFilterState({ globalSearch: '', columnFilters: [] })
    setPaginationState((prev) => ({ ...prev, page: 1 }))
  }, [])

  const refetch = useCallback(() => {
    if (!id) return

    dispatch(
      getListingAgProductsAsync({
        platformId: Number(id),
        page: paginationState.page,
        limit: paginationState.pageSize,
        search_string: filterState.globalSearch || '',
        sort_by: sortState?.field === 'product_name' ? 'name' : (sortState?.field || ''),
        sort_order: getSortOrder(sortState?.direction),
        columns: selectedApiColumns,
        columnFilters: apiColumnFilters,
      }) as any
    )
  }, [dispatch, id, paginationState.page, paginationState.pageSize, filterState.globalSearch, sortState, selectedApiColumns, apiColumnFilters])

  const handleDeleteVariantConfirm = async () => {
    if (!selectedVariantToDelete || !selectedVariantProduct) return
    try {
      setIsDeletingVariant(true)
      const res: any = await dispatch(
        deleteVariantAsync({
          productId: selectedVariantProduct.id,
          variantId: selectedVariantToDelete.id,
        })
      ).unwrap()

      const status = Number(res?.status)
      if (status === 200 || status === 201) {
        toast.success(res?.message || 'Variant deleted successfully')
        setShowDeleteVariantModal(false)
        setSelectedVariantToDelete(null)
        setVariantRefreshToggle(!variantRefreshToggle)
        refetch()
      } else {
        toast.error(
          res?.message || res?.data?.message || res?.errors?.detail || 'Failed to delete variant'
        )
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || error?.data?.errors?.detail || error?.message || 'Delete failed'
      )
      console.error('Variant delete failed:', error)
    } finally {
      setIsDeletingVariant(false)
    }
  }

  const platformName = (currentChannelData?.self_data?.name || '').toUpperCase()
  const enableBulkActions =
    !mainStore && !erpStore && isListingProductBulkActionPlatform(platformName)
  const bulkActionOptions = useMemo(
    () => getListingProductBulkActionOptions(platformName),
    [platformName]
  )

  const nameLinkBuilder = useCallback((row: ProductVariant) => {
    const rid = row?.id
    if (rid === undefined || rid === null) return ''
    const s = String(rid).trim()
    if (s === '') return ''

    // OMS store: product name links to the edit-product page (same as the Edit button).
    if (effectiveIsOmsStore) {
      return `/update-product/platform/${Number(id)}/productId/${s}`
    }

    // Non-OMS: return empty string ΓåÆ ProductTableLite renders plain, non-clickable text.
    return ''
  }, [effectiveIsOmsStore, id])

  if (error) {
    return (
      <div className='alert alert-danger d-flex align-items-center mb-4' role='alert'>
        <i className='bi bi-exclamation-triangle-fill me-3 fs-3'></i>
        <div>
          <strong>Error loading grouped lite data:</strong>
          <div className='mt-1'>{error}</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <ProductTableLite
        data={displayRows as ProductVariant[]}
        loading={loading}
        catalogColumns={catalogColumns}
        selectedColumns={selectedColumns}
        onColumnsChange={handleColumnsChange}
        onReorderColumns={handleReorderColumns}
        onPinColumn={handlePinColumn}
        getColumnByField={getColumnByField}
        sortState={sortState}
        onSortChange={handleSortChange}
        filterState={filterState}
        onFilterChange={handleFilterChange}
        paginationState={paginationState}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRefresh={refetch}
        onExportCSV={handleExportCSV}
        onResetColumns={handleResetColumns}
        platformId={platformId}
        channelId={id}
        mainStore={mainStore}
        erpStore={erpStore}
        tabsData={tabsData}
        onLinkClick={(row) => onLinkClick?.(row, 0)}
        enableBulkActions={enableBulkActions}
        bulkActionOptions={bulkActionOptions}
        selectedAction={selectedAction}
        onSelectedActionChange={setSelectedAction}
        selectedCount={selectedRowIds.length}
        selectionEnabled={enableBulkActions}
        selectedRowIds={selectedRowIds}
        onSelectedRowIdsChange={setSelectedRowIds}
        mandatoryColumns={mandatoryColumns}
        defaultVisibleFields={groupedLiteDefaultVisibleFields}
        onOpenColumnsConfig={() => {}}
        nameLinkBuilder={nameLinkBuilder}
        onGroupedLiteEditProduct={groupedLiteEditHandler}
        groupedLiteEditEnabled={groupedLiteEditEnabled}
        // isOmsStore drives variant column display; onGroupedLiteAddVariant handles zero-variant case.
        isOmsStore={Boolean(isOmsStore)}
        onGroupedLiteAddVariant={(row) => {
          // Navigates to product update page for a product that has zero variants instead of variant modal
          navigate(`/update-product/platform/${Number(id)}/productId/${row?.id}`, {
            state: {
              mode: 'edit',
              rowData: row,
              platformId: Number(id),
            },
          })
        }}
        onGroupedLiteVariantsClick={handleOpenGroupedLiteVariantsModal}
      />

      <CommonAlertModal
        title={`Are you sure you want to delete this variant?`}
        subtitle='This variant will be deleted permanently.'
        isVisible={showDeleteVariantModal}
        isTermsVisible={false}
        onSuccess={handleDeleteVariantConfirm}
        onCancel={() => {
          if (isDeletingVariant) return
          setShowDeleteVariantModal(false)
          setSelectedVariantToDelete(null)
        }}
        successBtnTitle={isDeletingVariant ? 'Deleting...' : 'Yes, Delete'}
        cancelBtnTitle='Cancel'
        successBtnClasses='btn btn-sm btn-danger m-2'
        successDisabled={isDeletingVariant}
      />

      {showVariantsModal && selectedVariantProduct && (
        <ProductAllVariantsModal
          show={showVariantsModal}
          onClose={handleCloseGroupedLiteVariantsModal}
          product={selectedVariantProduct}
          isOmsStore={effectiveIsOmsStore}
          platformId={id}
          goToAddVariant={(product) => {
            navigate(`/variant-create/platform/${Number(id)}/productId/${product?.id}`, {
              state: {
                mode: 'add',
                productId: product?.id,
                platformId: Number(id),
              },
            })
          }}
          onEditVariant={(variantRow, product) => {
            const editVariantId = variantRow?.id
            const editProductId = product?.id
            navigate(
              `/update-variant/platform/${Number(id)}/productId/${editProductId}/variantId/${editVariantId}`,
              {
                state: {
                  mode: 'edit',
                  editProductId,
                  editVariantId,
                },
              }
            )
          }}
          onDeleteVariant={(variant, product) => {
            setSelectedVariantToDelete(variant)
            setShowDeleteVariantModal(true)
          }}
          refreshToggle={variantRefreshToggle}
        />
      )}
    </>
  )
}

export default GroupedViewLiteContainer
